import React, { createContext, useContext, useMemo, useCallback, useState } from 'react';
import ExplainPage from './ExplainPage';

// F25 — cross-cutting explanations state. A thin wrapper over dashboardSettings:
// no Firestore code of its own. In demo mode handleUpdateDashboardSettings
// already keeps changes local-only, so dismissals simply don't persist there.
//
// The provider also hosts the single full-screen ExplainPage overlay; any
// descendant opens it via openPage('fire' | 'timeline' | 'simulation' |
// 'snapshots'). (OnboardingFlow renders outside this provider and hosts its
// own instance instead.)
const ExplainContext = createContext({
    enabled: false,
    shouldShow: () => false,
    dismiss: () => {},
    resetDismissed: () => {},
    setEnabled: () => {},
    dismissedCount: 0,
    openPage: () => {},
});

export const ExplainProvider = ({ dashboardSettings, onUpdateDashboardSettings, children }) => {
    const [openPageId, setOpenPageId] = useState(null);
    const [isPageOpen, setIsPageOpen] = useState(false);

    const dismissed = useMemo(
        () => (Array.isArray(dashboardSettings.dismissedExplanations) ? dashboardSettings.dismissedExplanations : []),
        [dashboardSettings.dismissedExplanations]
    );
    const enabled = dashboardSettings.explanationsEnabled !== false;

    const shouldShow = useCallback((id) => enabled && !dismissed.includes(id), [enabled, dismissed]);

    const dismiss = useCallback((id) => {
        if (dismissed.includes(id)) return;
        onUpdateDashboardSettings({ dismissedExplanations: [...dismissed, id] });
    }, [dismissed, onUpdateDashboardSettings]);

    const resetDismissed = useCallback(() => {
        onUpdateDashboardSettings({ dismissedExplanations: [] });
    }, [onUpdateDashboardSettings]);

    const setEnabled = useCallback((value) => {
        onUpdateDashboardSettings({ explanationsEnabled: !!value });
    }, [onUpdateDashboardSettings]);

    const openPage = useCallback((pageId) => {
        setOpenPageId(pageId);
        setIsPageOpen(true);
    }, []);

    const closePage = useCallback(() => {
        // Keep openPageId set during the exit animation; ExplainPage caches it anyway.
        setIsPageOpen(false);
    }, []);

    const value = useMemo(() => ({
        enabled, shouldShow, dismiss, resetDismissed, setEnabled, dismissedCount: dismissed.length, openPage,
    }), [enabled, shouldShow, dismiss, resetDismissed, setEnabled, dismissed.length, openPage]);

    return (
        <ExplainContext.Provider value={value}>
            {children}
            <ExplainPage feature={openPageId} isOpen={isPageOpen} onClose={closePage} />
        </ExplainContext.Provider>
    );
};

export const useExplain = () => useContext(ExplainContext);
